import { Fragment, useCallback, useMemo, useState } from "react";

import {
    LoanCollateralExpanded,
    AbfLoanExpanded,
    BsMetaUtil,
    calculateLoanHealth,
    canLenderReclaimCollateral,
    isSimpleLoan,
    LoanStatus,
    LoopPositionExpanded,
    calculateLoanLiquidationPrice,
    isLoanBorrower,
    getUnclaimedAssets,
    Loan,
    useLoopscaleWalletContext,
    isLoanComplete,
    StrategyExpandedInfo,
    useIsLoanLender,
    LoopExpanded,
    getZcCollateral,
    getLoanOrcaCollateral,
    getLoanRaydiumCollateral,
    isRaydiumMetadata,
    isWhirlpoolMetadata,
    getHealthStatus,
    LoanHealthStatus,
    getZcLedger,
    getPtTokenTermEnd,
    useOraclePrices,
    useWhirlpoolPositions,
    useRaydiumPositions,
    isWhirlpoolPosition,
    isRaydiumPosition,
    PriceFetchType,
    TokenListMetadata,
    isLoopLoan,
    getLoanSymbols,
    getLoopSymbols
} from "@bridgesplit/abf-react";
import {
    FALLBACK_METADATA,
    RaydiumPositionExpanded,
    UncertaintyType,
    WhirlpoolPositionExpanded
} from "@bridgesplit/abf-sdk";
import {
    useMemoizedKeyMap,
    Tooltip,
    TagText,
    BreadcrumbsInterface,
    Breadcrumbs,
    TextVariant,
    TagColor,
    TextColor,
    useAppPalette,
    Icon,
    Row,
    Text,
    FONT_SIZES,
    Image,
    TextButton,
    TagTextProps,
    StatColumn,
    Column,
    SkeletonRounded,
    TooltipText,
    Link,
    IconType
} from "@bridgesplit/ui";
import {
    firstElement,
    formatPercent,
    formatTokenAmount,
    removeDuplicatesByProperty,
    bsMath,
    TIME,
    formatUsd,
    formatDate,
    isBetweenNowAnd,
    isPastNow
} from "@bridgesplit/utils";
import { ArrowForwardOutlined, SwapHorizOutlined } from "@mui/icons-material";
import { BORROW_SLUG, COPY, LOAN_SLUG, PORTFOLIO_OVERVIEW_SLUG } from "app/constants";
import { Box } from "@mui/material";

import { getTokenImageSize, OverlappingMetadataImages, TokenImage, TokenSize } from "./asset";
import { PriceRangeStatus } from "./clmm";
import { parseRaydiumTicks } from "./raydium";
import { parseOrcaTicks } from "./whirlpool";
import { useLoanStatusTooltipText } from "../loan/util";

export enum LoanStatusFilter {
    All = "All"
}

export type LoanFilter = LoanStatus | LoanStatusFilter;

export const getLoanAssetPath = (loan: Loan | undefined) => (loan ? `${LOAN_SLUG}/${loan.address}` : BORROW_SLUG);

export function LoanBreadCrumbs({
    loanExpanded,
    isLoading
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    isLoading?: boolean;
}) {
    const paths: BreadcrumbsInterface[] = [
        { text: "Portfolio", path: PORTFOLIO_OVERVIEW_SLUG },
        { text: getLoanName(loanExpanded), path: getLoanAssetPath(loanExpanded?.loan) }
    ];

    return <Breadcrumbs loading={isLoading} paths={paths} />;
}

function getLoanName(loanExpanded: AbfLoanExpanded | undefined): string {
    const { principalSymbol, collateralSymbol } = getLoanSymbols(loanExpanded);
    return isLoopLoan(loanExpanded) ? collateralSymbol : principalSymbol;
}

export function getTagPropsFromLoanStatus(status: LoanStatus | undefined): {
    label: string | undefined;
    color: TagColor | undefined;
} {
    if (!status) return { label: undefined, color: undefined };
    if ([LoanStatus.DefaultedLoan].includes(status)) return { label: "Liquidated", color: "error" };
    if ([LoanStatus.RepaidLoan].includes(status)) return { label: "Closed", color: "body" };
    return { label: undefined, color: undefined };
}

export function isLoanUnknown(loanExpanded: AbfLoanExpanded | undefined) {
    return loanExpanded?.principalMetadata.mint === FALLBACK_METADATA.mint;
}

export function useHealthColor(healthStatus: LoanHealthStatus | undefined): string {
    const { success, warning, error } = useAppPalette();

    if (healthStatus === LoanHealthStatus.Healthy || healthStatus === undefined) {
        return success;
    } else if (healthStatus === LoanHealthStatus.Warning) {
        return warning;
    } else {
        return error;
    }
}

export function StrategyRepairTag({ strategyExpanded }: { strategyExpanded: StrategyExpandedInfo | undefined }) {
    const isMissingUpdate = isStrategyMissingUpdateTransaction(strategyExpanded);

    if (!isMissingUpdate) return null;
    return (
        <Tooltip
            title={`One of your ${COPY.STRATEGY_TERM.toLowerCase()} settings is missing. Please update your ${COPY.STRATEGY_TERM.toLowerCase()} to continue lending`}
        >
            <TagText>
                {COPY.STRATEGY_TERM} is inactive
                <Icon sx={{ color: (theme) => `${theme.palette.text.disabled} !important` }} type="tooltip" />
            </TagText>
        </Tooltip>
    );
}

export function isStrategyMissingUpdateTransaction(strategyExpanded: StrategyExpandedInfo | undefined): boolean {
    if (!strategyExpanded) return false;
    return !Object.keys(strategyExpanded.terms?.assetTerms ?? {}).length;
}

export function LoanHealthTag({
    loan,
    variant = "body1",
    textOnly = false
}: {
    loan: AbfLoanExpanded | undefined;
    variant?: TextVariant;
    textOnly?: boolean;
}) {
    const { textDisabled } = useAppPalette();
    const iconSx = {
        height: FONT_SIZES.body2,
        width: FONT_SIZES.body2
    };

    const healthDetails = calculateLoanHealth(loan);

    const healthColor = useHealthColor(healthDetails.loanHealthStatus);

    if (!loan) return null;

    const tagTextProps: Partial<TagTextProps> = { px: 1, py: 0.5, textVariant: variant, variant: "basic" };

    return (
        <Tooltip reverseColors title={<HealthTooltip healthDetails={healthDetails} loanExpanded={loan} />}>
            {textOnly ? (
                <Row spacing={0.5} sx={{ alignItems: "center" }}>
                    <Icon type="health" sx={{ ...iconSx, color: healthColor }} />
                    <Text variant={variant}>{formatPercent(healthDetails.health)}</Text>
                    <Icon sx={{ ...iconSx, color: textDisabled }} type="tooltip" />
                </Row>
            ) : (
                <TagText {...tagTextProps} customColor={healthColor}>
                    <Icon type="health" /> {formatPercent(healthDetails.health)}
                    <Icon sx={{ color: textDisabled }} type="tooltip" />
                </TagText>
            )}
        </Tooltip>
    );
}

export function LoanHealthBar({
    loan,
    variant = "body1"
}: {
    loan: AbfLoanExpanded | undefined;
    variant?: TextVariant;
}) {
    const healthDetails = calculateLoanHealth(loan);
    const healthColor = useHealthColor(healthDetails.loanHealthStatus);

    if (!loan) return null;

    const heart = (
        <Row spacing={0.5} sx={{ alignItems: "center" }}>
            <Icon type="health" sx={{ color: healthColor, fontSize: FONT_SIZES.body2 }} />
            <Text variant={variant}>{formatPercent(healthDetails.health)}</Text>
        </Row>
    );

    if (loan.status !== LoanStatus.OngoingLoan) {
        return heart;
    }

    return (
        <Tooltip reverseColors title={<HealthTooltip healthDetails={healthDetails} loanExpanded={loan} />}>
            <Row spacing={0.5} sx={{ alignItems: "center" }}>
                {heart}
                <Icon
                    sx={{
                        color: (theme) => `${theme.palette.text.disabled} !important`,
                        fontSize: FONT_SIZES.body2
                    }}
                    type="tooltip"
                />
            </Row>
        </Tooltip>
    );
}

export function HealthTooltip({
    healthDetails,
    variant = "body2",
    loanExpanded
}: {
    healthDetails: ReturnType<typeof calculateLoanHealth>;
    variant?: TextVariant;
    loanExpanded: AbfLoanExpanded | undefined;
}) {
    const liquidationPrice = calculateLoanLiquidationPrice(loanExpanded);

    return (
        <Column sx={{ minWidth: "230px" }} p={1} spacing={1}>
            <StatColumn
                captionVariant={variant}
                variant={variant}
                stats={[
                    {
                        caption: "Current LTV",
                        value: formatPercent(healthDetails.ltv)
                    },
                    {
                        caption: COPY.LIQUIDATION_THRESHOLD_TERM,
                        value: formatPercent(healthDetails.liquidationThreshold)
                    },
                    {
                        caption: COPY.LIQUIDATION_PRICE_TERM,
                        value: (
                            <LiquidationPrice
                                variant={variant}
                                liquidationPrice={liquidationPrice?.price ?? 0}
                                collateral={liquidationPrice?.collateral.metadata}
                                principal={loanExpanded?.principalMetadata}
                            />
                        )
                    }
                ]}
            />
        </Column>
    );
}

export function LoanHealthText({
    loan,
    variant = "body1"
}: {
    loan: AbfLoanExpanded | undefined;
    variant?: TextVariant;
}) {
    const { health, loanHealthStatus } = calculateLoanHealth(loan);
    const healthColor = useHealthColor(loanHealthStatus);

    if (!loan) return null;

    return (
        <Text variant={variant} sx={{ svg: { color: healthColor, fontSize: "inherit" } }}>
            <Icon type="health" /> {formatPercent(health)}
        </Text>
    );
}

function getAssetPriceDenomination({
    loopExpanded,
    isPrincipalPriced,
    principalPrice,
    collateralPrice
}: {
    loopExpanded: LoopExpanded | undefined;
    isPrincipalPriced: boolean;
    principalPrice: number | undefined;
    collateralPrice: number | undefined;
}): {
    denominationAmount: number | undefined;
    denominatedDecimals: number | undefined;
    denominatedSymbol: string | undefined;
} {
    if (!loopExpanded) {
        return {
            denominationAmount: undefined,
            denominatedDecimals: undefined,
            denominatedSymbol: undefined
        };
    }

    const principalToken = loopExpanded?.principalToken;
    const collateralToken = loopExpanded?.collateralToken;

    const denominationAmount = isPrincipalPriced
        ? bsMath.div(collateralPrice, principalPrice)
        : bsMath.div(principalPrice, collateralPrice);

    const denominatedDecimals = isPrincipalPriced ? principalToken.decimals : collateralToken.decimals;

    const denominatedSymbol = isPrincipalPriced ? principalToken.symbol : collateralToken.symbol;

    return {
        denominationAmount,
        denominatedDecimals,
        denominatedSymbol
    };
}

export function CurrencyPrice({
    loopPosition,
    isLoading,
    variant = "body1"
}: {
    loopPosition: LoopPositionExpanded | undefined;
    isLoading?: boolean;
    variant?: TextVariant;
}) {
    const { principalUsdOraclePrice, collateralUsdOraclePrice } = useLoanOraclePriceQuotes(
        loopPosition?.loopExpanded?.principalToken,
        getZcCollateral(loopPosition?.loanExpanded)
    );

    const [isPrincipalPriced, setIsPrincipalPriced] = useState(true);
    if (loopPosition?.loopExpanded === undefined || !loopPosition?.loanExpanded) return null;

    let tokenImages = [
        BsMetaUtil.getImage(loopPosition?.loopExpanded?.principalToken),
        BsMetaUtil.getImage(loopPosition?.loopExpanded?.collateralToken)
    ];

    if (!isPrincipalPriced) {
        tokenImages = tokenImages.reverse();
    }

    const { denominationAmount, denominatedDecimals, denominatedSymbol } = getAssetPriceDenomination({
        loopExpanded: loopPosition?.loopExpanded,
        isPrincipalPriced,
        principalPrice: principalUsdOraclePrice,
        collateralPrice: collateralUsdOraclePrice
    });

    if (isLoading) return <SkeletonRounded width={100} height={FONT_SIZES["body1"]} />;

    if (!denominationAmount) return null;

    return (
        <Row spacing={1}>
            <Text variant={variant}>
                {formatTokenAmount(denominationAmount, {
                    decimals: denominatedDecimals,
                    symbol: denominatedSymbol
                })}
            </Text>

            <Row spacing={0.5}>
                {tokenImages.map((src, i) => (
                    <Fragment key={i}>
                        {i !== 0 && <Text color="disabled">/</Text>}
                        <Image size={`${FONT_SIZES[variant]}px`} variant="circle" src={src} />
                    </Fragment>
                ))}
            </Row>
            <TextButton variant={variant} onClick={() => setIsPrincipalPriced((prev) => !prev)} color="disabled">
                <SwapHorizOutlined />
            </TextButton>
        </Row>
    );
}

export function LiquidationPrice({
    liquidationPrice,
    collateral,
    principal,
    percentChange: percentChangeProps,
    variant = "body1",
    isLoading
}: {
    liquidationPrice: number;
    collateral: TokenListMetadata | undefined;
    principal: TokenListMetadata | undefined;
    percentChange?: number;
    variant?: TextVariant;
    isLoading?: boolean;
}) {
    const [flippedPrice, setFlippedPrice] = useState(false);
    let tokenImages = [BsMetaUtil.getImage(principal), BsMetaUtil.getImage(collateral)];

    const percentChange = percentChangeProps && flippedPrice ? percentChangeProps * -1 : percentChangeProps;

    const priceFormatted = flippedPrice
        ? `${formatTokenAmount(1 / liquidationPrice, { decimals: 3 })}`
        : `${formatTokenAmount(liquidationPrice, { decimals: 3 })}`;

    if (flippedPrice) {
        tokenImages = tokenImages.reverse();
    }

    if (isLoading) return <SkeletonRounded width={100} height={FONT_SIZES[variant]} />;

    return (
        <Row spacing={1}>
            <Row spacing={0.5}>
                {!!percentChange && priceFormatted.length < 8 && (
                    <Text color={percentChange < 0 ? "error" : "success"} variant="caption">
                        {formatPercent(percentChange, { includePlus: true, customDecimals: 1 })}
                    </Text>
                )}
                <Text variant={variant}>{priceFormatted}</Text>
            </Row>

            <Row spacing={0.5}>
                {tokenImages.map((src, i) => (
                    <Fragment key={i}>
                        {i !== 0 && <Text color="disabled">/</Text>}
                        <Image size={`${FONT_SIZES[variant]}px`} variant="circle" src={src} />
                    </Fragment>
                ))}
            </Row>
            <TextButton variant={variant} onClick={() => setFlippedPrice((prev) => !prev)} color="disabled">
                <SwapHorizOutlined />
            </TextButton>
        </Row>
    );
}

export function LoanDailyCarry({
    loanExpanded,
    isLender
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    isLender?: boolean;
}) {
    const [showTokenTerms, setShowTokenTerms] = useState(false);

    const { dollarPerDay, tokenPerDay, tokenMetadata } = useMemo(() => {
        const collateral = getZcCollateral(loanExpanded);
        const collateralUsd = bsMath.mul(collateral?.amount, collateral?.usdPrice);
        const collateralApr = loanExpanded?.collateralYieldPct;

        const debtUsd = bsMath.mul(getZcLedger(loanExpanded)?.ledgerDebt.total, loanExpanded?.principalUsdPrice);
        const debtApr = getZcLedger(loanExpanded)?.apy ?? 0;

        const dollarPerDay = calculateNetDailyCarry({ isLender, collateralUsd, collateralApr, debtUsd, debtApr });

        const tokenPerDay = isLender
            ? calculateDailyCarryInCollateralTerms(dollarPerDay, loanExpanded?.principalUsdPrice)
            : calculateDailyCarryInCollateralTerms(dollarPerDay, collateral?.usdPrice);
        const tokenMetadata = isLender ? loanExpanded?.principalMetadata : collateral?.metadata;

        return { dollarPerDay, tokenPerDay, tokenMetadata };
    }, [loanExpanded, isLender]);

    const canToggle = tokenPerDay !== undefined;

    return (
        <Row spacing={0.5}>
            {canToggle && (
                <TextButton color="disabled" onClick={() => setShowTokenTerms((prev) => !prev)}>
                    <SwapHorizOutlined />
                </TextButton>
            )}
            <Text>
                {showTokenTerms && tokenPerDay !== undefined
                    ? BsMetaUtil.formatAmount(tokenMetadata, tokenPerDay)
                    : formatUsd(dollarPerDay)}
            </Text>
        </Row>
    );
}

function calculateNetDailyCarry({
    isLender,
    collateralUsd,
    collateralApr,
    debtUsd,
    debtApr
}: {
    isLender: boolean | undefined;
    collateralUsd: number | undefined;
    collateralApr: number | undefined;
    debtUsd: number | undefined;
    debtApr: number | undefined;
}) {
    if (debtUsd === undefined || debtApr === undefined) return 0;
    const daily = TIME.DAY / TIME.YEAR;
    if (isLender) return debtUsd * debtApr * daily;
    if (collateralUsd === undefined || collateralApr === undefined) return 0;
    return (collateralUsd * collateralApr - debtUsd * debtApr) * daily;
}

function calculateDailyCarryInCollateralTerms(
    dollarPerDay: number | undefined,
    collateralUsdPrice: number | undefined
) {
    if (dollarPerDay === undefined || !collateralUsdPrice) return undefined;
    return bsMath.div(dollarPerDay, collateralUsdPrice);
}

export function useUserLoanStatus(loan: AbfLoanExpanded | undefined) {
    const {
        data: { userWalletAddress: user }
    } = useLoopscaleWalletContext();

    const status = loan?.status;

    const isLender = useIsLoanLender(loan, user);

    if (!loan) return undefined;

    const isBorrower = isLoanBorrower(loan, user);

    const unclaimedAssets = getUnclaimedAssets(loan);

    const canLenderClaimPayments = (() => {
        if (isSimpleLoan(loan)) return false;
    })();

    const unclaimedAssetsExist = !!unclaimedAssets?.length;

    const canBorrowerClaimAssets =
        unclaimedAssetsExist && status === LoanStatus.RepaidLoan && isBorrower && user && !loan.lenders.includes(user);
    const canLenderClaimAssets = unclaimedAssetsExist && canLenderReclaimCollateral(loan) && isLender;
    return {
        isBorrower,
        isLender,
        canLenderClaimPayments,
        canBorrowerClaimAssets,
        canLenderClaimAssets,
        unclaimedAssets
    };
}

export function HealthText({
    health,
    variant = "body1",
    loanHealthStatus,
    loading
}: HealthDetails & { variant?: TextVariant; loading?: boolean }) {
    const healthColor = useHealthColor(loanHealthStatus);
    return (
        <Text loading={health === undefined || loading} sx={{ color: healthColor }} variant={variant}>
            {formatPercent(health)}
        </Text>
    );
}

export interface HealthDetails {
    health: number | undefined;
    loanHealthStatus: LoanHealthStatus;
    ltv: number | undefined;
}

interface HealthChangeProps {
    originalHealth: HealthDetails | undefined;
    newHealth: HealthDetails | undefined;
    variant?: TextVariant;
    loading?: boolean;
}

export function HealthChange({ originalHealth, newHealth, variant = "body1", loading }: HealthChangeProps) {
    if (!originalHealth || !newHealth) return null;
    if (formatPercent(originalHealth.health) === formatPercent(newHealth.health) || loading)
        return <HealthText variant={variant} {...originalHealth} />;
    return (
        <Row spacing={1}>
            <HealthText variant={variant} {...originalHealth} />
            <Text color="disabled" variant="body2">
                <ArrowForwardOutlined />
            </Text>
            <HealthText variant={variant} {...newHealth} />
        </Row>
    );
}

export function LtvText({
    ltv,
    variant = "body1",
    liquidationThreshold,
    strategyLtv
}: {
    ltv: number | undefined;
    variant?: TextVariant;
    liquidationThreshold: number | undefined;
    strategyLtv: number | undefined;
}) {
    const ltvColor = useHealthColor(getHealthStatus(ltv, strategyLtv, liquidationThreshold));
    return (
        <Text loading={ltv === undefined} sx={{ color: ltvColor }} variant={variant}>
            {formatPercent(ltv)}
        </Text>
    );
}

export function ClmmStatus({ loan }: { loan: AbfLoanExpanded | undefined }) {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const isLender = useIsLoanLender(loan, activeWallet);

    const collateral = getZcCollateral(loan);
    if (isLender) {
        return null;
    }
    if (isWhirlpoolMetadata(collateral?.metadata)) {
        return <OrcaStatus loan={loan} />;
    }
    if (isRaydiumMetadata(collateral?.metadata)) {
        return <RaydiumStatus loan={loan} />;
    }

    return null;
}

function RaydiumStatus({ loan }: { loan: AbfLoanExpanded | undefined }) {
    const raydiumPosition = getLoanRaydiumCollateral(loan);
    const ticks = raydiumPosition ? parseRaydiumTicks(raydiumPosition) : undefined;

    return (
        <PriceRangeStatus
            iconOnly
            tickCurrentIndex={ticks?.currentTick}
            tickLower={ticks?.tickLower}
            tickUpper={ticks?.tickUpper}
        />
    );
}

function OrcaStatus({ loan }: { loan: AbfLoanExpanded | undefined }) {
    const whirlpoolPosition = getLoanOrcaCollateral(loan);
    const ticks = whirlpoolPosition ? parseOrcaTicks(whirlpoolPosition) : undefined;

    return (
        <PriceRangeStatus
            iconOnly
            tickCurrentIndex={ticks?.currentTick}
            tickLower={ticks?.tickLower}
            tickUpper={ticks?.tickUpper}
        />
    );
}

export function OutstandingDebt({ loanExpanded }: { loanExpanded: AbfLoanExpanded | undefined }) {
    const ledger = getZcLedger(loanExpanded);
    return <Text>{BsMetaUtil.formatAmount(loanExpanded?.principalMetadata, ledger?.ledgerDebt.total)}</Text>;
}

const PT_EXPIRATION_THRESHOLD = TIME.DAY * 7;

export function PtCollateralExpirationStatus({ collateralToken }: { collateralToken: TokenListMetadata | undefined }) {
    const maturityDate = getPtTokenTermEnd(collateralToken);

    if (!maturityDate) return null;

    const isExpired = isPastNow(maturityDate);
    const isExpiringWithin = isBetweenNowAnd(maturityDate, PT_EXPIRATION_THRESHOLD);

    if (!isExpired && !isExpiringWithin) return null;

    const maturityDateText = formatDate(maturityDate, "date", {
        showTime: "never"
    });

    const color: TextColor = isExpired ? "error" : "warning";
    const caption = isExpired ? `Matured on ${maturityDateText}.` : `Approaching maturity on ${maturityDateText}.`;

    return (
        <TooltipText icon={false} helpText={caption} sx={{ gap: 0.5 }} color={color}>
            <Icon type="date" />
        </TooltipText>
    );
}

export function useLoanOraclePriceQuotes(
    principalMetadata: TokenListMetadata | undefined,
    collateral: LoanCollateralExpanded | undefined
): {
    principalUsdOraclePrice: number | undefined;
    collateralUsdOraclePrice: number | undefined;
} {
    const principalMint = principalMetadata?.mint;
    const collateralMint = collateral?.assetMint ?? collateral?.metadata?.mint;

    const isOrca = isWhirlpoolMetadata(collateral?.metadata);
    const isRaydium = isRaydiumMetadata(collateral?.metadata);

    const { data: whirlpools } = useWhirlpoolPositions(isOrca && collateralMint ? [collateralMint] : undefined, {
        skip: !isOrca || !collateralMint
    });
    const { data: raydiumPools } = useRaydiumPositions(isRaydium && collateralMint ? [collateralMint] : undefined, {
        skip: !isRaydium || !collateralMint
    });

    const whirlpoolGetKey = useCallback((w: WhirlpoolPositionExpanded) => w.position.positionMint, []);
    const raydiumNftGetKey = useCallback((r: RaydiumPositionExpanded) => r.position.nftMint, []);

    const positionToWhirlpool = useMemoizedKeyMap(whirlpools ?? [], whirlpoolGetKey);
    const positionNftToPool = useMemoizedKeyMap(raydiumPools ?? [], raydiumNftGetKey);

    const { getUsdPrice } = useOraclePrices([principalMint, collateralMint], PriceFetchType.Twap);

    const principalUsdOraclePrice = getUsdPrice(principalMint, UncertaintyType.Add);

    let collateralUsdOraclePrice = getUsdPrice(collateralMint, UncertaintyType.Subtract);

    if (isOrca && collateralMint) {
        const w = positionToWhirlpool?.get(collateralMint);
        if (isWhirlpoolPosition(w) && w.totalPrice !== undefined) {
            collateralUsdOraclePrice = w.totalPrice;
        }
    } else if (isRaydium && collateralMint) {
        const r = positionNftToPool?.get(collateralMint);
        if (isRaydiumPosition(r) && r.totalPrice !== undefined) {
            collateralUsdOraclePrice = r.totalPrice;
        }
    }

    return {
        principalUsdOraclePrice,
        collateralUsdOraclePrice
    };
}

export function LoanStatusTag({
    loanExpanded,
    variant = "body1",
    hideTooltip = false
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    variant?: TextVariant;
    hideTooltip?: boolean;
}) {
    const { color, label } = getTagPropsFromLoanStatus(loanExpanded?.status);
    const tooltipText = useLoanStatusTooltipText(loanExpanded);

    if (!label) return null;

    return (
        <TagText
            variant="colored"
            textVariant={variant}
            color={color}
            helpText={!hideTooltip ? tooltipText : undefined}
        >
            {label}
        </TagText>
    );
}

function getTokenSizeFromVariant(variant: TextVariant) {
    if (variant === "h1" || variant === "h2") {
        return "lg";
    }
    if (variant === "h3") {
        return "md";
    }
    return "sm";
}

type NameBaseProps = {
    href?: string;
    variant?: TextVariant;
    condensed?: boolean;
    fullSymbolOnHover?: boolean;
    hideIcon?: boolean;
    iconSize?: TokenSize;
    iconBorderColor?: string;
    isLoading?: boolean;
};

type LoopNameProps = {
    loopExpanded: LoopExpanded | undefined;
} & NameBaseProps;

type LoanNameProps = {
    loanExpanded: AbfLoanExpanded | undefined;
} & NameBaseProps;

type PositionNameTextProps = {
    primarySymbol: string;
    primarySymbolTooltip?: string;
    secondarySymbol: string;
    secondarySymbolTooltip?: string;
    variant?: TextVariant;
    isLoading?: boolean;
};
function PositionNameText({
    primarySymbol,
    primarySymbolTooltip,
    secondarySymbol,
    secondarySymbolTooltip,
    variant = "body1",
    isLoading
}: PositionNameTextProps) {
    return (
        <>
            <Tooltip title={primarySymbolTooltip}>
                <Text variant={variant} loading={isLoading} loadingWidth="100px">
                    {primarySymbol}
                </Text>
            </Tooltip>
            <Tooltip title={secondarySymbolTooltip}>
                <Text variant={variant} color="caption" loading={isLoading} loadingWidth="100px">
                    {secondarySymbol}
                </Text>
            </Tooltip>
        </>
    );
}

// Supports Loops and Loans but use LoopName where possible for better type safety
export function LoanName({
    loanExpanded,
    href,
    variant = "body1",
    condensed,
    fullSymbolOnHover = true,
    hideIcon,
    iconSize,
    isLoading
}: LoanNameProps) {
    const tokenMetadata = useLoanTokenMetadata(loanExpanded);
    const {
        principalSymbol,
        collateralSymbol: longCollateralSymbol,
        shortCollateralSymbol
    } = getLoanSymbols(loanExpanded);
    const { symbol: collateralSymbol, tooltip: collateralSymbolTooltip } = getSymbolDisplay({
        symbol: longCollateralSymbol,
        shortSymbol: shortCollateralSymbol,
        condensed,
        fullSymbolOnHover
    });
    const tokenIconSize = iconSize ?? getTokenSizeFromVariant(variant);
    const isLoop = isLoopLoan(loanExpanded);

    return (
        <Row spacing={0.5}>
            {!hideIcon && <PositionTokenIcon {...tokenMetadata} size={tokenIconSize} isLoop={isLoop} />}
            {href ? (
                <Link to={href} sx={{ textDecoration: "none", color: "inherit" }}>
                    <PositionNameText
                        primarySymbol={isLoop ? collateralSymbol : principalSymbol}
                        primarySymbolTooltip={isLoop ? collateralSymbolTooltip : undefined}
                        secondarySymbol={isLoop ? principalSymbol : collateralSymbol}
                        secondarySymbolTooltip={!isLoop ? collateralSymbolTooltip : undefined}
                        variant={variant}
                        isLoading={isLoading}
                    />
                </Link>
            ) : (
                <PositionNameText
                    primarySymbol={isLoop ? collateralSymbol : principalSymbol}
                    primarySymbolTooltip={isLoop ? collateralSymbolTooltip : undefined}
                    secondarySymbol={isLoop ? principalSymbol : collateralSymbol}
                    secondarySymbolTooltip={!isLoop ? collateralSymbolTooltip : undefined}
                    variant={variant}
                    isLoading={isLoading}
                />
            )}
        </Row>
    );
}

function getSymbolDisplay({
    symbol,
    shortSymbol,
    condensed,
    fullSymbolOnHover
}: {
    symbol: string;
    shortSymbol: string | undefined;
    condensed?: boolean;
    fullSymbolOnHover?: boolean;
}) {
    if (!condensed || !shortSymbol || symbol === shortSymbol) return { symbol: symbol, tooltip: undefined };
    return { symbol: shortSymbol, tooltip: fullSymbolOnHover ? symbol : undefined };
}

export function LoopName({
    loopExpanded,
    href,
    variant = "body1",
    condensed,
    fullSymbolOnHover = true,
    hideIcon,
    iconSize,
    isLoading
}: LoopNameProps) {
    if (!loopExpanded) return null;
    const {
        collateralSymbol: longCollateralSymbol,
        shortCollateralSymbol,
        shortPrincipalSymbol
    } = getLoopSymbols(loopExpanded);
    const { symbol: collateralSymbol, tooltip: collateralSymbolTooltip } = getSymbolDisplay({
        symbol: longCollateralSymbol,
        shortSymbol: shortCollateralSymbol,
        condensed,
        fullSymbolOnHover
    });
    const principalSymbol = shortPrincipalSymbol;
    const tokenIconSize = iconSize ?? getTokenSizeFromVariant(variant);

    return (
        <Row spacing={0.5}>
            {!hideIcon && (
                <PositionTokenIcon
                    collateral={[loopExpanded.collateralToken]}
                    principal={[loopExpanded.principalToken]}
                    size={tokenIconSize}
                    isLoop
                />
            )}
            {href ? (
                <Link to={href} sx={{ textDecoration: "none", color: "inherit" }}>
                    <PositionNameText
                        primarySymbol={collateralSymbol}
                        primarySymbolTooltip={collateralSymbolTooltip}
                        secondarySymbol={principalSymbol}
                        variant={variant}
                        isLoading={isLoading}
                    />
                </Link>
            ) : (
                <PositionNameText
                    primarySymbol={collateralSymbol}
                    primarySymbolTooltip={collateralSymbolTooltip}
                    secondarySymbol={principalSymbol}
                    variant={variant}
                    isLoading={isLoading}
                />
            )}
        </Row>
    );
}

type PositionTokenIconProps = {
    collateral: TokenListMetadata[] | undefined;
    principal: TokenListMetadata[] | undefined;
    size: TokenSize;
    isLoop?: boolean;
};

type LoopTokenIconProps = {
    loopExpanded: LoopExpanded | undefined;
} & Omit<PositionTokenIconProps, "collateral" | "principal" | "isLoop">;

type LoanTokenIconProps = {
    loanExpanded: AbfLoanExpanded | undefined;
} & Omit<PositionTokenIconProps, "collateral" | "principal" | "isLoop">;

export function PositionTokenIcon({ collateral, principal, size, isLoop }: PositionTokenIconProps) {
    const { cardBackground } = useAppPalette();
    const tokenSize = getTokenImageSize(size);

    if (isLoop) {
        const badgeMultiplier = 0.6;
        const topOffset = (1 - badgeMultiplier) * tokenSize;
        const leftOffset = 0.5 * tokenSize;
        return (
            <Box sx={{ position: "relative", width: tokenSize, height: tokenSize, marginRight: `${leftOffset}px` }}>
                <TokenImage
                    size={badgeMultiplier * tokenSize}
                    sx={{
                        position: "absolute",
                        top: topOffset,
                        left: leftOffset,
                        zIndex: 1,
                        borderRadius: "100%",
                        border: "1px solid",
                        borderColor: cardBackground,
                        boxSizing: "content-box"
                    }}
                    metadata={firstElement(principal)}
                />
                <TokenImage size={tokenSize} metadata={firstElement(collateral)} />
            </Box>
        );
    }

    if (!collateral?.length || !principal?.length) {
        return <SkeletonRounded width={tokenSize} height={tokenSize} />;
    }

    return (
        <OverlappingMetadataImages
            sort={false}
            disableTooltip
            size={`${tokenSize}px`}
            metadata={[...principal, ...collateral]}
        />
    );
}

// Use LoopName or LoanName where possible
export function LoopTokenImages({ loopExpanded, ...props }: LoopTokenIconProps) {
    return (
        <PositionTokenIcon
            collateral={loopExpanded && [loopExpanded.collateralToken]}
            principal={loopExpanded && [loopExpanded.principalToken]}
            {...props}
            isLoop
        />
    );
}

// Use LoanName where possible
export function LoanTokenImages({ loanExpanded, ...props }: LoanTokenIconProps) {
    const tokenMetadata = useLoanTokenMetadata(loanExpanded);
    return <PositionTokenIcon {...tokenMetadata} {...props} />;
}

export function useLoanTokenMetadata(loanExpanded: AbfLoanExpanded | undefined) {
    return useMemo(() => {
        if (!loanExpanded) return { collateral: [], principal: [] };

        if (isLoanComplete(loanExpanded)) {
            const uniqueCollateral = removeDuplicatesByProperty(
                loanExpanded.pastCollateral.map((c) => c.metadata),
                "mint"
            );
            return { collateral: uniqueCollateral, principal: [loanExpanded.principalMetadata] };
        } else {
            const uniqueCollateral = removeDuplicatesByProperty(
                loanExpanded.collateral.map((c) => c.metadata),
                "mint"
            );
            return { collateral: uniqueCollateral, principal: [loanExpanded.principalMetadata] };
        }
    }, [loanExpanded]);
}

export type EventTagKind =
    | "deposit"
    | "withdrawRequest"
    | "withdrawProcessed"
    | "withdrawCancel"
    | "borrow"
    | "repay"
    | "addCollateral"
    | "removeCollateral"
    | "adjustTerms"
    | "sellLoan"
    | "liquidate"
    | "fullLiquidation"
    | "partialLiquidation"
    | "fee"
    | "activity"
    | "withdrawFulfill"
    | "withdrawInstant";

const EVENT_TAG_CONFIG: Record<EventTagKind, { label: string; color: TagColor; icon: IconType | undefined }> = {
    deposit: { label: "Deposit", color: "secondary", icon: "arrow-down" },
    withdrawInstant: { label: "Withdraw", color: "warning", icon: "arrow-diagonal" },
    withdrawRequest: { label: "Withdraw request", color: "disabled", icon: "clock" },
    withdrawProcessed: { label: "Withdraw processed", color: "warning", icon: "arrow-diagonal" },
    withdrawCancel: { label: "Withdraw cancel", color: "warning", icon: "arrow-diagonal" },
    borrow: { label: "Borrow", color: "warning", icon: "arrow-diagonal" },
    repay: { label: "Repay", color: "success", icon: "arrow-down" },
    addCollateral: { label: "Add collateral", color: "info", icon: "receive" },
    removeCollateral: { label: "Remove collateral", color: "warning", icon: "remove" },
    adjustTerms: { label: "Adjust terms", color: "body", icon: "adjust" },
    sellLoan: { label: "Sell loan", color: "body", icon: undefined },
    liquidate: { label: "Liquidate", color: "error", icon: "error" },
    fullLiquidation: { label: "Full liquidation", color: "error", icon: "error" },
    partialLiquidation: { label: "Partial liquidation", color: "error", icon: "error" },
    fee: { label: "Fee", color: "body", icon: undefined },
    activity: { label: "Activity", color: "body", icon: undefined },
    withdrawFulfill: { label: "Withdraw fulfill", color: "warning", icon: "arrow-diagonal" }
};

export function EventTag({
    kind,
    label,
    variant = "body1"
}: {
    kind: EventTagKind;
    label?: string;
    variant?: TextVariant;
}) {
    const config = EVENT_TAG_CONFIG[kind];
    return (
        <Row spacing={1} sx={{ alignItems: "center" }}>
            {config.icon && (
                <TagText
                    variant="colored"
                    textVariant="body2"
                    color={config.color}
                    borderRadius={100}
                    px={0.5}
                    sx={{ textAlign: "center" }}
                >
                    <Icon
                        type={config.icon}
                        sx={{ fontSize: "inherit", display: "inline-block", verticalAlign: "middle" }}
                    />
                </TagText>
            )}

            <Text variant={variant}>{label ?? config.label}</Text>
        </Row>
    );
}
